import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { XeroInvoice } from '../../../types/xero';
import { parseXeroDate } from '../../../utils/xeroDate';

interface NetCashFlowViewProps {
  invoices: XeroInvoice[];
  bills: XeroInvoice[];
  startDate: Date;
  endDate: Date;
}

function getMonthsBetween(start: Date, end: Date): { year: number; month: number }[] {
  const out: { year: number; month: number }[] = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const endFirst = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cur <= endFirst) {
    out.push({ year: cur.getFullYear(), month: cur.getMonth() });
    cur.setMonth(cur.getMonth() + 1);
  }
  return out;
}

interface MonthlyNet {
  month: string;
  monthLabel: string;
  monthFullLabel: string;
  cashIn: number;
  cashOut: number;
  net: number;
  cumulative: number;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

const formatCompact = (amount: number) => {
  if (Math.abs(amount) >= 1000) {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', notation: 'compact', maximumFractionDigits: 1 }).format(amount);
  }
  return formatCurrency(amount);
};

export function NetCashFlowView({ invoices, bills, startDate, endDate }: NetCashFlowViewProps) {
  const monthlyData = useMemo(() => {
    const monthList = getMonthsBetween(startDate, endDate);
    const months: MonthlyNet[] = monthList.map(({ year, month }) => {
      const date = new Date(year, month, 1);
      return {
        month: `${year}-${String(month + 1).padStart(2, '0')}`,
        monthLabel: date.toLocaleDateString('en-AU', { month: 'short' }),
        monthFullLabel: date.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }),
        cashIn: 0,
        cashOut: 0,
        net: 0,
        cumulative: 0,
      };
    });

    const addToMonth = (date: Date | null, amount: number, type: 'in' | 'out') => {
      // Refunds arrive as NEGATIVE payment rows (Stripe subscription refunds) and
      // must NET against income, not be dropped. Only a zero/NaN row is noise.
      if (!date || !Number.isFinite(amount) || amount === 0) return;
      if (date < startDate || date > endDate) return;
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthData = months.find(m => m.month === monthKey);
      if (monthData) {
        if (type === 'in') monthData.cashIn += amount;
        else monthData.cashOut += amount;
      }
    };

    invoices.forEach((invoice) => {
      if (invoice.Status === 'VOIDED') return;
      if (invoice.Payments && invoice.Payments.length > 0) {
        invoice.Payments.forEach(p => addToMonth(parseXeroDate(p.Date), p.Amount, 'in'));
      } else if (invoice.AmountPaid > 0) {
        addToMonth(parseXeroDate((invoice as any).Date || (invoice as any).DateString), invoice.AmountPaid, 'in');
      }
    });

    bills.forEach((bill) => {
      if (bill.Status === 'VOIDED') return;
      if (bill.Payments && bill.Payments.length > 0) {
        bill.Payments.forEach(p => addToMonth(parseXeroDate(p.Date), p.Amount, 'out'));
      } else if (bill.AmountPaid > 0) {
        addToMonth(parseXeroDate((bill as any).Date || (bill as any).DateString), bill.AmountPaid, 'out');
      }
    });

    let cumulative = 0;
    months.forEach(m => {
      m.net = m.cashIn - m.cashOut;
      cumulative += m.net;
      m.cumulative = cumulative;
    });

    return months;
  }, [invoices, bills, startDate, endDate]);

  const avgNet = monthlyData.reduce((s, m) => s + m.net, 0) / monthlyData.length;
  const currentMonth = monthlyData[monthlyData.length - 1];
  const prevMonth = monthlyData[monthlyData.length - 2];
  const trendDirection = currentMonth && prevMonth
    ? currentMonth.net >= prevMonth.net ? 'up' : 'down'
    : 'flat';

  const chartOptions: ApexOptions = {
    chart: {
      type: 'area',
      toolbar: { show: false },
      fontFamily: 'inherit',
      sparkline: { enabled: false },
    },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: [3, 2] },
    fill: {
      type: ['gradient', 'solid'],
      gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 90, 100] },
      opacity: [1, 0],
    },
    colors: ['#50CD89', '#3E97FF'],
    xaxis: {
      categories: monthlyData.map(m => m.monthLabel),
      labels: { style: { colors: 'var(--bs-gray-600)', fontSize: '11px' } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        formatter: (val) => formatCompact(val),
        style: { colors: 'var(--bs-gray-600)', fontSize: '11px' },
      },
    },
    tooltip: {
      theme: 'dark',
      custom: ({ dataPointIndex }) => {
        const m = monthlyData[dataPointIndex];
        return `<div class="px-3 py-2">
          <div class="fw-bold mb-1">${m.monthFullLabel}</div>
          <div class="text-success">Net: ${formatCurrency(m.net)}</div>
          <div class="text-info">Cumulative: ${formatCurrency(m.cumulative)}</div>
          <div class="text-muted fs-8 mt-1">In: ${formatCurrency(m.cashIn)} / Out: ${formatCurrency(m.cashOut)}</div>
        </div>`;
      },
    },
    grid: {
      borderColor: 'var(--bs-gray-200)',
      strokeDashArray: 4,
      yaxis: { lines: { show: true } },
      xaxis: { lines: { show: false } },
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '12px',
      markers: { width: 10, height: 10, radius: 10 },
      itemMargin: { horizontal: 15 },
      labels: { colors: 'var(--bs-gray-700)' },
    },
  };

  const chartSeries = [
    { name: 'Monthly Net', data: monthlyData.map(m => Math.round(m.net * 100) / 100) },
    { name: 'Cumulative', data: monthlyData.map(m => Math.round(m.cumulative * 100) / 100) },
  ];

  return (
    <div className="row g-4">
      <div className="col-12 col-lg-4">
        <div className="d-flex flex-column gap-4">
          <div>
            <span className="text-muted fs-7 d-block mb-1">This Month Net</span>
            <div className={`fs-2 fw-bold ${(currentMonth?.net ?? 0) >= 0 ? 'text-success' : 'text-danger'}`}>
              {currentMonth ? formatCurrency(currentMonth.net) : '-'}
            </div>
            <div className="d-flex align-items-center gap-1 mt-1">
              <i className={`bi ${trendDirection === 'up' ? 'bi-arrow-up-right text-success' : 'bi-arrow-down-right text-danger'} fs-7`} />
              <span className={`fs-8 ${trendDirection === 'up' ? 'text-success' : 'text-danger'}`}>
                vs last month
              </span>
            </div>
          </div>
          <div>
            <span className="text-muted fs-7 d-block mb-1">Period Average</span>
            <div className={`fs-3 fw-bold ${avgNet >= 0 ? 'text-success' : 'text-danger'}`}>
              {formatCurrency(avgNet)}
            </div>
            <span className="text-muted fs-8">per month</span>
          </div>
          <div className="pt-2 border-top">
            <span className="text-muted fs-7 d-block mb-1">Cumulative Position</span>
            <div className={`fs-3 fw-bold ${(currentMonth?.cumulative ?? 0) >= 0 ? 'text-success' : 'text-danger'}`}>
              {currentMonth ? formatCurrency(currentMonth.cumulative) : '-'}
            </div>
            <span className="text-muted fs-8">over period</span>
          </div>
        </div>
      </div>
      <div className="col-12 col-lg-8">
        <Chart options={chartOptions} series={chartSeries} type="area" height={220} />
      </div>
    </div>
  );
}

export default NetCashFlowView;
